/* global wp, tinyMCE, kmAwards, grecaptcha */

import Notices from './notices';
import Utils from './utils';
import Tabs from './tabs';

class UserForms {
  constructor() {
    this.form = document.querySelector('.js-km-awards-user-form');

    if (!this.form) {
      return;
    }

    this.globals = kmAwards;

    this.classnames = {
      loading: 'is-loading',
      weak: 'is-weak',
      strong: 'is-strong',
      medium: 'is-medium',
      hidden: 'is-hidden',
      hasHidden: 'has-hidden',
      hasImage: 'has-image',
      isActive: 'is-active',
      disabled: 'is-disabled',
      primary: 'is-primary',
    };

    window.addEventListener('DOMContentLoaded', () => {
      this.initTabs();
      this.initForms();
    });
  }

  initTabs() {
    const tabs = this.form.querySelector('.js-km-awards-tabs');

    if (tabs) {
      new Tabs(tabs);
    }
  }

  initForms() {
    this.wp = typeof wp === 'undefined' ? null : wp;
    this.tinyMCE = typeof tinyMCE === 'undefined' ? null : tinyMCE;

    this.isLoading = false;
    this.hasStrongPassword = false;
    this.customAjaxData = {};

    this.notices = new Notices();

    this.type = this.form.getAttribute('data-type');
    this.subtype = this.form.getAttribute('data-subtype') || '';
    this.passwordStrength = this.wp?.passwordStrength;

    this.recaptcha = {
      instance: typeof grecaptcha === 'undefined' ? null : grecaptcha,
      siteKey: this.globals.recaptchaKey,
      field: this.form.querySelector('.js-recaptcha'),
    };
    this.addEntryButtons = this.form.querySelectorAll('.is-add-entry');
    this.addressNotice = document.querySelector('.is-address-notice');

    this.passwordStrengthChecker();
    this.imageUploadHandler();

    this.form.addEventListener('submit', this.submitHandler.bind(this));
  }

  checkValidityHandler() {
    const firstInvalid = this.form.querySelector(':invalid');

    if (firstInvalid) {
      const tab = firstInvalid.closest('.js-km-awards-tabs-content-item');

      if (tab && !tab.classList.contains(this.classnames.isActive)) {
        const tabs = tab.closest('.js-km-awards-tabs');
        const tabId = tab.getAttribute('data-tab');
        const tabNavLink = tabs.querySelector(`.js-km-awards-tabs-nav-link[data-tab="${tabId}"]`);

        tabNavLink.click();
      }

      this.form.reportValidity();
      firstInvalid.focus();
    }
  }

  generateRecaptchaToken = () => {
    return new Promise((res) => {
      this.recaptcha.instance.ready(() => {
        this.recaptcha.instance
          .execute(this.recaptcha.siteKey, { action: 'km_awards' })
          .then((token) => {
            return res(token);
          });
      });
    });
  };

  submitHandler(event) {
    event.preventDefault();

    this.submitterType = event.submitter.dataset.type || 'default';

    if (this.isLoading) {
      return;
    }

    if (!this.form.checkValidity()) {
      this.checkValidityHandler();
      return;
    }

    this.notices.hideNotice();

    if (this.tinyMCE) {
      tinyMCE.triggerSave();
    }

    this.getFormValues();

    if (this.type === 'profile' && this.formValues.user_password.trim() === '') {
      this.hasStrongPassword = true;
    }

    if (!this.hasStrongPassword) {
      this.notices.showNotice({
        message: 'Please set a strong password.',
      });
      return;
    }

    if (this.submitterType === 'add-another') {
      if (
        !confirm(
          `This will save the entry and clear the form to create a new one. Are you sure you want to continue?`,
        )
      ) {
        return;
      }
    }

    this.buttons = this.form.querySelectorAll(
      'button[type="submit"], a.km-awards-btn:not(.is-disabled)',
    );

    this.buttons.forEach((button) => {
      button.classList.add(
        button === event.submitter ? this.classnames.loading : this.classnames.disabled,
      );
    });

    if (this.recaptcha.instance && this.recaptcha.field) {
      this.generateRecaptchaToken().then((token) => {
        this.updateCustomAjaxData({
          recaptcha_response: token,
        });
        this.sendForm();
      });
    } else {
      this.sendForm();
    }

    this.isLoading = true;
  }

  sendForm() {
    const endpoint = `user-${this.type}`;

    Utils.ajaxRequest(endpoint, {
      strong_password: this.hasStrongPassword,
      subtype: this.subtype,
      submitter_type: this.submitterType,
      ...this.formValues,
      ...this.customAjaxData,
    }).then((response) => {
      if (response?.error === 1) {
        this.notices.showNotice({
          message: response.message,
          dismissDelay: -1,
        });
      } else {
        if (response?.redirect) {
          window.location.href = response.redirect;
        } else {
          this.notices.showNotice({
            message: response.message,
            type: 'success',
            dismissDelay: -1,
          });
        }

        if (response?.data?.resetForm || response?.resetForm) {
          this.form.reset();
        }

        if (response?.data?.reload || response?.reload) {
          setTimeout(() => {
            window.location.reload();
          }, 2000);
        }
      }

      if (this.addEntryButtons.length) {
        this.addEntryButtons.forEach((button) => {
          button.classList.remove(this.classnames.disabled);
          button.classList.add(this.classnames.primary);
        });

        if (this.addressNotice) {
          this.addressNotice.classList.remove(this.classnames.isActive);
        }
      }

      this.buttons.forEach((button) => {
        button.classList.remove(this.classnames.loading, this.classnames.disabled);
      });
      this.isLoading = false;
    });
  }

  getFormValues() {
    const formData = new FormData(this.form);
    this.formValues = {};

    formData.forEach((value, key) => {
      this.formValues[key] = value;
    });
  }

  updateCustomAjaxData(data) {
    this.customAjaxData = {
      ...this.customAjaxData,
      ...data,
    };
  }

  generatePassword = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+';
    let password = '';
    const array = new Uint32Array(16);
    window.crypto.getRandomValues(array);
    for (let i = 0; i < array.length; i++) {
      password += chars[array[i] % chars.length];
    }
    return password;
  };

  passwordStrengthChecker() {
    this.password = this.form.querySelector('#user_password');
    this.passwordRepeat = this.form.querySelector('#user_password_repeat');
    this.strengthMeter = this.form.querySelector('.js-km-awards-password-strength');
    this.generateBtn = this.form.querySelector('.js-km-awards-generate-password');
    this.copiedMsg = this.form.querySelector('.js-km-awards-password-copied');

    if (!this.passwordStrength || !this.password || !this.strengthMeter) {
      this.hasStrongPassword = true;
      return;
    }

    if (this.generateBtn) {
      this.generateBtn.addEventListener('click', () => {
        const newPassword = this.generatePassword();

        this.password.value = newPassword;
        this.password.type = 'text';

        if (this.passwordRepeat) {
          this.passwordRepeat.value = newPassword;
          this.passwordRepeat.type = 'text';
        }

        const event = new Event('keyup');
        this.password.dispatchEvent(event);

        if (navigator.clipboard) {
          navigator.clipboard.writeText(newPassword);
        }

        if (this.copiedMsg) {
          clearTimeout(this.copiedMsgTimeout);
          this.copiedMsg.classList.remove(this.classnames.hidden);
          this.copiedMsgTimeout = setTimeout(() => {
            this.copiedMsg.classList.add(this.classnames.hidden);
          }, 2000);
        }
      });
    }

    this.password.addEventListener('keyup', () => {
      const strength = this.passwordStrength.meter(
        this.password.value,
        this.passwordStrength.userInputDisallowedList(),
      );
      const length = this.password.value.length;
      const strengthText = this.getPasswordStrength(strength, length);

      if (strengthText) {
        this.strengthMeter.classList.remove(this.classnames.hidden);
        this.strengthMeter.textContent = strengthText;
        this.hasStrongPassword = strength >= 3;
      }
      if (strength > 3) {
        this.strengthMeter.classList.remove(this.classnames.weak);
        this.strengthMeter.classList.remove(this.classnames.medium);
        this.strengthMeter.classList.add(this.classnames.strong);
      } else if (strength > 2) {
        this.strengthMeter.classList.remove(this.classnames.weak);
        this.strengthMeter.classList.remove(this.classnames.strong);
        this.strengthMeter.classList.add(this.classnames.medium);
      } else {
        this.strengthMeter.classList.remove(this.classnames.medium);
        this.strengthMeter.classList.remove(this.classnames.strong);
        this.strengthMeter.classList.add(this.classnames.weak);
      }
    });
  }

  getPasswordStrength = (strengthValue, length) => {
    const tooWeak = 'Too weak. Add another word or two.';

    let strenght = strengthValue;

    if (length < 9) {
      strenght = 2;
    }

    switch (strenght) {
      case 3:
        return 'Medium. Good password.';
      case 4:
        return 'Strong. Very good password.';
      default:
        return tooWeak;
    }
  };

  imageUploadHandler() {
    this.images = this.form.querySelectorAll('.js-km-awards-image-upload');

    if (!this.images.length) {
      return;
    }

    this.imagesToRemove = [];
    this.imagesToUpload = [];

    this.images.forEach((imageField) => {
      this.imageFieldEventsHandlers(imageField);
    });

    this.updateCustomAjaxData({
      images_to_remove: this.imagesToRemove,
    });
  }

  imageFieldEventsHandlers = (imageField) => {
    const { maxFileSize, fileTypes } = this.form.dataset;
    const allowedFileTypes = JSON.parse(fileTypes);

    const input = imageField.querySelector('input');
    const preview = imageField.querySelector('img');
    const currentImageUrl = preview.getAttribute('src');
    const removeBtn = imageField.querySelector('.js-km-awards-image-remove');
    const errorContainer = imageField.parentElement.querySelector('.js-km-awards-image-error');

    const inputName = input.getAttribute('name');

    input.addEventListener('change', () => {
      const reader = new FileReader();
      const file = input.files[0];

      errorContainer.textContent = '';
      imageField.classList.add(this.classnames.loading);

      reader.addEventListener('load', () => {
        imageField.classList.remove(this.classnames.loading);
        imageField.classList.add(this.classnames.hasImage);

        preview.src = reader.result;

        if (file.size > maxFileSize) {
          errorContainer.textContent = 'The file is too big.';
          return;
        }

        if (!allowedFileTypes.includes(file.type)) {
          errorContainer.textContent = 'The file type is not allowed.';
          return;
        }

        if (!this.imagesToUpload.includes(inputName)) {
          this.imagesToUpload.push(inputName);
        }

        if (currentImageUrl) {
          this.imagesToRemove.push(inputName);
        }
      });

      reader.readAsDataURL(file);
    });

    removeBtn.addEventListener('click', () => {
      input.value = '';
      preview.src = currentImageUrl;
      imageField.classList.remove(this.classnames.hasImage);

      if (currentImageUrl) {
        this.imagesToRemove.push(inputName);
      }

      if (this.imagesToUpload.includes(inputName)) {
        const index = this.imagesToUpload.indexOf(inputName);

        if (index > -1) {
          this.imagesToUpload.splice(index, 1);
        }
      }
    });
  };
}

export default UserForms;
